import { createApi } from '@reduxjs/toolkit/query/react';
import dynamicBaseQueryWithLogger from '@/services/utils/dynamicBaseQueryWithLogger';
import { handlerErrorResponse } from '@/services/utils/handlers';
import { generateIdempotencyKey } from '@/services/utils/idempotency';
import { notifyAppError } from '@/services/utils/notifyAppError';

// Se usa el microservicio de POS si está definido en variables de entorno.
const BASE_URL = process.env.NEXT_PUBLIC_POS_API_URL ?? 'http://localhost:8083';

type ErrorsMap = Record<string, string[]>;

export interface ApiResponseBase<T = unknown> {
  success: boolean;
  message: string;
  data: T;
  errors?: ErrorsMap;
}

export type PageResult<T> = {
  items: T[];
  total: number;
  page: number;
  page_size: number;
};

export type PaginationParams = {
  page?: number;
  page_size?: number;
};

export type CashSessionStatus = 'open' | 'closing' | 'closed' | 'cancelled';

export type CashSessionFilters = PaginationParams & {
  status?: CashSessionStatus;
  business_date?: string; // YYYY-MM-DD
  opened_date?: string; // YYYY-MM-DD
  closed_date?: string; // YYYY-MM-DD
  date?: string; // Alias temporal legacy
  timezone?: string;
};

export type DailyClosureQueryParams = {
  business_date?: string;
  date?: string; // Alias temporal legacy
  timezone?: string;
};

export type POSTerminal = {
  id: number;
  company_id?: number;
  branch_id?: number;
  code: string;
  name: string;
  is_active: boolean;
};

export type CashSessionRecord = {
  id: number;
  pos_terminal_id: number;
  pos_terminal_name: string;
  user_id: number;
  opened_by_username: string;
  opened_by_name?: string | null;
  closed_by_username?: string | null;
  closed_by_name: string | null;
  opened_at: string | null;
  business_date: string;
  business_timezone: string;
  closeout_hour_snapshot: number;
  closed_at: string | null;
  opening_amount: number;
  closing_amount: number | null;
  status: CashSessionStatus;
  notes: string;
  expected_amount: number | null;
  cash_difference: number | null;
  payment_summary_id?: string | null;
  payment_summary_snapshot?: string | null;
  payment_summary_received_at?: string | null;
  closed_by_user_id: number | null;
  closing_notes: string | null;
  is_stale: boolean;
  can_close: boolean;
  opened_by_current_user: boolean;
  block_reason: string;
};

export type OpenCashSessionInput = {
  pos_terminal_id: number;
  opening_amount: number;
  notes?: string | null;
};

export type OpenCashSessionArgs = OpenCashSessionInput & {
  timezone?: string;
};

export type PaymentSummarySnapshot = {
  payment_summary_id: string;
  cash_sales: number;
  cash_refunds: number;
  card_sales: number;
  transfer_sales: number;
  currency_code?: string | null;
  generated_at?: string | null;
};

export type CloseCashSessionInput = {
  session_id: number;
  closing_amount: number;
  notes?: string | null;
  payments_summary: PaymentSummarySnapshot;
};

export type CloseCashSessionPreviewInput = {
  session_id: number;
  closing_amount: number;
  notes?: string | null;
  payments_summary: PaymentSummarySnapshot;
};

export type CashSessionCloseSummary = {
  session_id: number;
  expected_amount?: number | null;
  closing_amount: number;
  cash_difference?: number | null;
  cash_sales?: number | null;
  cash_refunds?: number | null;
  paid_in?: number | null;
  paid_out?: number | null;
  closed_by_user_id?: number | null;
  closed_at?: string | null;
};

export type DailyClosureSessionDetail = {
  cash_session_id: number;
  pos_terminal_id: number;
  expected_amount: number;
  closing_amount: number;
  cash_difference: number;
  closed_by_user_id: number | null;
  closing_notes?: string | null;
  payment_summary_id?: string | null;
  payment_summary_snapshot?: string | null;
  payment_summary_received_at?: string | null;
};

export type DailyClosureResult = {
  id: number;
  business_date: string;
  date: string;
  company_id: number;
  branch_id: number;
  cash_initial_base: number;
  cash_expected_end: number;
  cash_closing_end: number;
  cash_end_difference: number;
  sessions: DailyClosureSessionDetail[];
};

export type DailyClosureExistsResult = {
  company_id?: number | null;
  branch_id?: number | null;
  business_date?: string | null;
  date?: string | null;
  exists: boolean;
  id?: number | null;
};

export type DailyClosurePrintSessionDetail = {
  cash_session_id: number;
  pos_terminal_id: number;
  pos_terminal_name?: string | null;
  expected_amount: number;
  closing_amount: number;
  cash_difference: number;
  closed_by_user_id?: number | null;
  opened_by_username?: string | null;
  opened_by_name?: string | null;
  closed_by_username?: string | null;
  closed_by_name?: string | null;
  opened_at?: string | null;
  closed_at?: string | null;
  closing_notes?: string | null;
  payment_summary_id?: string | null;
  payment_summary_snapshot?: string | null;
  payment_summary_received_at?: string | null;
};

export type DailyClosurePrintContext = {
  id: number;
  business_date: string;
  date: string;
  company_id: number;
  branch_id: number;
  cash_initial_base: number;
  cash_expected_end: number;
  cash_closing_end: number;
  cash_end_difference: number;
  created_by: number;
  created_at?: string | null;
  notes?: string | null;
  sessions: DailyClosurePrintSessionDetail[];
};

export type DailyClosurePreviewMethodTotal = {
  code: string;
  name: string;
  amount: number;
};

export type DailyClosurePreviewSession = {
  id: number;
  status: CashSessionStatus;
  notes: string;
  can_close: boolean;
  is_stale: boolean;
  pos_terminal_id: number;
  pos_terminal_name: string;
  business_date?: string | null;
  business_timezone?: string | null;
  closeout_hour_snapshot?: number | null;
  opened_at?: string | null;
  closed_at?: string | null;
  opened_by_username: string;
  opened_by_name: string;
  closed_by_user_id?: number | null;
  closed_by_username?: string | null;
  closed_by_name?: string | null;
  opening_amount: number;
  expected_amount: number;
  closing_amount: number;
  cash_difference: number;
};

export type DailyClosurePreviewResult = {
  business_date: string;
  date: string;
  company_id: number;
  branch_id: number;
  cash_initial_base: number;
  cash_expected_end: number;
  cash_closing_end: number;
  cash_end_difference: number;
  payments_total: number;
  payments_by_method: DailyClosurePreviewMethodTotal[];
  sessions_count: number;
  sessions: DailyClosurePreviewSession[];
};

export type SaleLineInput = {
  inventory_id: number;
  item_sku?: string | null;
  item_name?: string | null;
  quantity: number;
  unit_price: number;
  discount?: number | null;
  tax_rate?: number | null;
  tax_amount?: number | null;
  line_total: number;
};

export type SalePaymentInput = {
  amount: number;
  payment_date_iso: string;
  payment_method_id?: number | null;
  payment_method_code?: string | null;
  is_deposit?: boolean;
  notes?: string | null;
  reference?: string | null;
  tendered_amount?: number | null;
  change_amount?: number | null;
};

export type SaleInput = {
  pos_terminal_id: number;
  cash_session_id: number;
  customer_id?: number | null;
  source_type?: string | null;
  source_id?: number | null;
  status: 'open' | 'completed' | 'cancelled' | 'refunded';
  subtotal: number;
  discount_total: number;
  tax_total?: number | null;
  grand_total: number;
  currency_code: string;
  exchange_rate: number;
  created_at?: string | null;
  completed_at?: string | null;
  lines: SaleLineInput[];
  payment: SalePaymentInput;
};

/** Se tipa la respuesta del commit de venta en POS. */
export type SaleCommitResult = {
  commit_id: string;
  status: string;
  sale_id: number;
};

export type SaleReturnLineInput = {
  sale_line_id: number;
  inventory_id: number;
  quantity_returned: number;
  unit_price: number;
  discount?: number | null;
  tax_rate?: number | null;
  tax_amount?: number | null;
  line_total: number;
  restock_action: 'to_stock' | 'scrap' | 'repair';
  reason?: string | null;
};

export type SaleReturnInput = {
  sale_id: number;
  status: string;
  reason?: string | null;
  total_amount: number;
  return_date: string;
  lines: SaleReturnLineInput[];
};

export type SaleReturnRecord = SaleReturnInput & {
  id: number;
};

export type SaleReturnRestockAction = 'to_stock' | 'scrap' | 'repair';
export type SaleReturnOperationalStatus =
  | 'pending'
  | 'stock_applied'
  | 'refunded'
  | 'closed'
  | 'cancelled'
  | 'inventory_failed'
  | 'refund_failed';
export type SaleReturnInventoryStatus = 'pending' | 'applied' | 'failed' | 'not_required';
export type SaleReturnRefundStatus = 'pending' | 'applied' | 'failed' | 'not_required';

export type SaleReturnListItem = {
  id: number;
  company_id: number;
  branch_id: number;
  sale_id: number;
  status: string;
  document_status: string;
  operational_status: SaleReturnOperationalStatus | string;
  inventory_status: SaleReturnInventoryStatus | string;
  refund_status: SaleReturnRefundStatus | string;
  reason?: string | null;
  total_amount: number;
  return_date: string;
  closed_at?: string | null;
  sale_status?: string | null;
  sale_grand_total: number;
  lines_count: number;
  items_qty_total: number;
  to_stock_qty_total: number;
  non_stock_qty_total: number;
};

export type SaleReturnDetailLine = {
  id: number;
  sale_return_id: number;
  sale_line_id: number;
  inventory_id: number;
  item_sku?: string | null;
  item_name?: string | null;
  quantity_returned: number;
  unit_price: number;
  discount: number;
  tax_rate: number;
  tax_amount: number;
  line_total: number;
  restock_action: SaleReturnRestockAction | string;
  reason?: string | null;
  inventory_apply_status: string;
  ledger_id?: number | null;
  inventory_applied_at?: string | null;
};

export type SaleReturnDetail = SaleReturnListItem & {
  inventory_applied_at?: string | null;
  inventory_error_code?: string | null;
  inventory_error_cause?: string | null;
  inventory_idempotency_key?: string | null;
  refund_applied_at?: string | null;
  refund_error_code?: string | null;
  refund_error_cause?: string | null;
  refund_idempotency_key?: string | null;
  refund_payment_id?: number | null;
  closure_blockers: string[];
  lines: SaleReturnDetailLine[];
};

export type SaleReturnListQuery = PaginationParams & {
  operational_status?: SaleReturnOperationalStatus | string;
};

export type SaleReturnInventoryReturnResponse = {
  idempotency_key: string;
  status: string;
  sale_return_id: number;
  applied_lines: number;
  inventory_status: string;
  idempotency_status?: string | null;
  stage?: string | null;
  error?: string | null;
  cause?: string | null;
};

export type CreateSaleReturnRefundInput = {
  refund_date: string;
  refund_method_id?: number | null;
  refund_method_code?: string | null;
  notes?: string | null;
  reference?: string | null;
};

export type SaleReturnRefundResponse = {
  idempotency_key: string;
  status: string;
  sale_return_id: number;
  payment_id?: number | null;
  refund_status: string;
  refunded_amount: number;
  idempotency_status?: string | null;
  stage?: string | null;
  error?: string | null;
  cause?: string | null;
};

export type SalesBySessionsResult = {
  sale_ids: number[];
  total: number;
};

type SalesBySessionsArgs = {
  cash_session_ids: string | Array<number | string>;
  status?: string;
};

export type SaleSummaryBatchRequest = {
  ids: number[];
};

export type SaleSummaryBatchTopItem = {
  sku: string;
  name: string;
  qty: number;
  line_total: number;
};

export type SaleSummaryBatchItem = {
  id: number;
  status: string;
  created_at: string;
  cash_session_id: number;
  pos_terminal_id: number;
  customer_id?: number | null;
  currency_code: string;
  grand_total: number;
  lines_count: number;
  items_qty_total: number;
  top_items?: SaleSummaryBatchTopItem[];
};

export type SaleSummaryBatchResult = {
  items: SaleSummaryBatchItem[];
  missing_ids?: number[];
};

export type SaleDetailLine = {
  id: number;
  sale_id: number;
  inventory_id: number;
  item_sku?: string | null;
  item_name?: string | null;
  quantity: number;
  unit_price: number;
  discount: number;
  tax_rate: number;
  tax_amount: number;
  line_total: number;
  quantity_returned: number;
  quantity_returnable: number;
};

export type SaleDetail = {
  id: number;
  company_id: number;
  branch_id: number;
  pos_terminal_id: number;
  cash_session_id: number;
  customer_id?: number | null;
  source_type?: string | null;
  source_id?: number | null;
  status: string;
  subtotal: number;
  discount_total: number;
  tax_total: number;
  grand_total: number;
  currency_code: string;
  exchange_rate: number;
  created_at?: string | null;
  completed_at?: string | null;
  lines: SaleDetailLine[];
};

type NormalizedRecord = Record<string, unknown>;

const asRecord = (value: unknown): NormalizedRecord =>
  value && typeof value === 'object' ? (value as NormalizedRecord) : {};

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const asNullableString = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

const asNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return fallback;
};

const asNullableNumber = (value: unknown): number | null => {
  const parsed = asNumber(value, Number.NaN);
  return Number.isFinite(parsed) ? parsed : null;
};

const asBoolean = (value: unknown): boolean =>
  typeof value === 'boolean' ? value : Boolean(value);

const readBusinessDate = (record: NormalizedRecord): string =>
  asString(record.business_date ?? record.date, '');

const normalizeCashSessionRecord = (value: unknown): CashSessionRecord | null => {
  const record = asRecord(value);
  const id = asNumber(record.id, 0);
  const terminalId = asNumber(record.pos_terminal_id, 0);
  if (id <= 0 || terminalId <= 0) {
    return null;
  }

  return {
    id,
    pos_terminal_id: terminalId,
    pos_terminal_name: asString(record.pos_terminal_name, ''),
    user_id: asNumber(record.user_id, 0),
    opened_by_username: asString(record.opened_by_username, ''),
    opened_by_name: asNullableString(record.opened_by_name),
    closed_by_username: asNullableString(record.closed_by_username),
    closed_by_name: asNullableString(record.closed_by_name),
    opened_at: asNullableString(record.opened_at),
    business_date: readBusinessDate(record),
    business_timezone: asString(record.business_timezone, 'UTC'),
    closeout_hour_snapshot: asNumber(record.closeout_hour_snapshot, 0),
    closed_at: asNullableString(record.closed_at),
    opening_amount: asNumber(record.opening_amount, 0),
    closing_amount: asNullableNumber(record.closing_amount),
    status:
      asString(record.status) === 'open' ||
      asString(record.status) === 'closing' ||
      asString(record.status) === 'closed' ||
      asString(record.status) === 'cancelled'
        ? (asString(record.status) as CashSessionStatus)
        : 'open',
    notes: asString(record.notes, ''),
    expected_amount: asNullableNumber(record.expected_amount),
    cash_difference: asNullableNumber(record.cash_difference),
    payment_summary_id: asNullableString(record.payment_summary_id),
    payment_summary_snapshot: asNullableString(record.payment_summary_snapshot),
    payment_summary_received_at: asNullableString(record.payment_summary_received_at),
    closed_by_user_id: asNullableNumber(record.closed_by_user_id),
    closing_notes: asNullableString(record.closing_notes),
    is_stale: asBoolean(record.is_stale),
    can_close: asBoolean(record.can_close),
    opened_by_current_user: asBoolean(record.opened_by_current_user),
    block_reason: asString(record.block_reason, ''),
  };
};

const normalizeCashSessionPageResult = (
  resp?: ApiResponseBase<PageResult<unknown>>,
): PageResult<CashSessionRecord> => {
  const raw = resp?.data;
  const items = Array.isArray(raw?.items)
    ? raw.items
        .map((entry) => normalizeCashSessionRecord(entry))
        .filter((entry): entry is CashSessionRecord => Boolean(entry))
    : [];

  return {
    items,
    total: asNumber(raw?.total, items.length),
    page: asNumber(raw?.page, 1),
    page_size: asNumber(raw?.page_size, items.length),
  };
};

const normalizeDailyClosureSessionDetail = (value: unknown): DailyClosureSessionDetail | null => {
  const record = asRecord(value);
  const sessionId = asNumber(record.cash_session_id, 0);
  const terminalId = asNumber(record.pos_terminal_id, 0);
  if (sessionId <= 0 || terminalId <= 0) {
    return null;
  }

  return {
    cash_session_id: sessionId,
    pos_terminal_id: terminalId,
    expected_amount: asNumber(record.expected_amount, 0),
    closing_amount: asNumber(record.closing_amount, 0),
    cash_difference: asNumber(record.cash_difference, 0),
    closed_by_user_id: asNullableNumber(record.closed_by_user_id),
    closing_notes: asNullableString(record.closing_notes),
    payment_summary_id: asNullableString(record.payment_summary_id),
    payment_summary_snapshot: asNullableString(record.payment_summary_snapshot),
    payment_summary_received_at: asNullableString(record.payment_summary_received_at),
  };
};

const normalizeDailyClosurePrintSessionDetail = (
  value: unknown,
): DailyClosurePrintSessionDetail | null => {
  const record = asRecord(value);
  const sessionId = asNumber(record.cash_session_id, 0);
  const terminalId = asNumber(record.pos_terminal_id, 0);
  if (sessionId <= 0 || terminalId <= 0) {
    return null;
  }

  return {
    cash_session_id: sessionId,
    pos_terminal_id: terminalId,
    pos_terminal_name: asNullableString(record.pos_terminal_name),
    expected_amount: asNumber(record.expected_amount, 0),
    closing_amount: asNumber(record.closing_amount, 0),
    cash_difference: asNumber(record.cash_difference, 0),
    closed_by_user_id: asNullableNumber(record.closed_by_user_id),
    opened_by_username: asNullableString(record.opened_by_username),
    opened_by_name: asNullableString(record.opened_by_name),
    closed_by_username: asNullableString(record.closed_by_username),
    closed_by_name: asNullableString(record.closed_by_name),
    opened_at: asNullableString(record.opened_at),
    closed_at: asNullableString(record.closed_at),
    closing_notes: asNullableString(record.closing_notes),
    payment_summary_id: asNullableString(record.payment_summary_id),
    payment_summary_snapshot: asNullableString(record.payment_summary_snapshot),
    payment_summary_received_at: asNullableString(record.payment_summary_received_at),
  };
};

const normalizeDailyClosurePreviewSession = (
  value: unknown,
  fallbackBusinessDate: string,
): DailyClosurePreviewSession | null => {
  const record = asRecord(value);
  const id = asNumber(record.id, 0);
  const terminalId = asNumber(record.pos_terminal_id, 0);
  if (id <= 0 || terminalId <= 0) {
    return null;
  }

  return {
    id,
    status:
      asString(record.status) === 'open' ||
      asString(record.status) === 'closing' ||
      asString(record.status) === 'closed' ||
      asString(record.status) === 'cancelled'
        ? (asString(record.status) as CashSessionStatus)
        : 'closed',
    notes: asString(record.notes, ''),
    can_close: asBoolean(record.can_close),
    is_stale: asBoolean(record.is_stale),
    pos_terminal_id: terminalId,
    pos_terminal_name: asString(record.pos_terminal_name, ''),
    business_date: asString(record.business_date, fallbackBusinessDate),
    business_timezone: asNullableString(record.business_timezone),
    closeout_hour_snapshot: asNullableNumber(record.closeout_hour_snapshot),
    opened_at: asNullableString(record.opened_at),
    closed_at: asNullableString(record.closed_at),
    opened_by_username: asString(record.opened_by_username, ''),
    opened_by_name: asString(record.opened_by_name, ''),
    closed_by_user_id: asNullableNumber(record.closed_by_user_id),
    closed_by_username: asNullableString(record.closed_by_username),
    closed_by_name: asNullableString(record.closed_by_name),
    opening_amount: asNumber(record.opening_amount, 0),
    expected_amount: asNumber(record.expected_amount, 0),
    closing_amount: asNumber(record.closing_amount, 0),
    cash_difference: asNumber(record.cash_difference, 0),
  };
};

const normalizeDailyClosureResult = (value: unknown): DailyClosureResult => {
  const record = asRecord(value);
  const businessDate = readBusinessDate(record);
  const rawSessions = Array.isArray(record.sessions) ? record.sessions : [];

  return {
    id: asNumber(record.id, 0),
    business_date: businessDate,
    date: asString(record.date, businessDate),
    company_id: asNumber(record.company_id, 0),
    branch_id: asNumber(record.branch_id, 0),
    cash_initial_base: asNumber(record.cash_initial_base, 0),
    cash_expected_end: asNumber(record.cash_expected_end, 0),
    cash_closing_end: asNumber(record.cash_closing_end, 0),
    cash_end_difference: asNumber(record.cash_end_difference, 0),
    sessions: rawSessions
      .map((entry) => normalizeDailyClosureSessionDetail(entry))
      .filter((entry): entry is DailyClosureSessionDetail => Boolean(entry)),
  };
};

const normalizeDailyClosurePreviewResult = (value: unknown): DailyClosurePreviewResult => {
  const record = asRecord(value);
  const businessDate = readBusinessDate(record);
  const rawMethods = Array.isArray(record.payments_by_method) ? record.payments_by_method : [];
  const rawSessions = Array.isArray(record.sessions) ? record.sessions : [];

  return {
    business_date: businessDate,
    date: asString(record.date, businessDate),
    company_id: asNumber(record.company_id, 0),
    branch_id: asNumber(record.branch_id, 0),
    cash_initial_base: asNumber(record.cash_initial_base, 0),
    cash_expected_end: asNumber(record.cash_expected_end, 0),
    cash_closing_end: asNumber(record.cash_closing_end, 0),
    cash_end_difference: asNumber(record.cash_end_difference, 0),
    payments_total: asNumber(record.payments_total, 0),
    payments_by_method: rawMethods
      .map((entry) => {
        const method = asRecord(entry);
        return {
          code: asString(method.code, ''),
          name: asString(method.name, ''),
          amount: asNumber(method.amount, 0),
        };
      })
      .filter((entry) => Number.isFinite(entry.amount)),
    sessions_count: asNumber(record.sessions_count, rawSessions.length),
    sessions: rawSessions
      .map((entry) => normalizeDailyClosurePreviewSession(entry, businessDate))
      .filter((entry): entry is DailyClosurePreviewSession => Boolean(entry)),
  };
};

const normalizeDailyClosureExistsResult = (value: unknown): DailyClosureExistsResult => {
  const record = asRecord(value);
  const candidateID = record.id ?? record.closure_id ?? record.daily_closure_id;
  const businessDate = readBusinessDate(record);

  return {
    company_id: asNullableNumber(record.company_id),
    branch_id: asNullableNumber(record.branch_id),
    business_date: businessDate || null,
    date: asString(record.date, businessDate || '') || null,
    exists: asBoolean(record.exists),
    id: asNullableNumber(candidateID),
  };
};

const normalizeSaleDetailLine = (value: unknown): SaleDetailLine | null => {
  const record = asRecord(value);
  const id = asNumber(record.id, 0);
  if (id <= 0) return null;

  return {
    id,
    sale_id: asNumber(record.sale_id, 0),
    inventory_id: asNumber(record.inventory_id, 0),
    item_sku: asNullableString(record.item_sku),
    item_name: asNullableString(record.item_name),
    quantity: asNumber(record.quantity, 0),
    unit_price: asNumber(record.unit_price, 0),
    discount: asNumber(record.discount, 0),
    tax_rate: asNumber(record.tax_rate, 0),
    tax_amount: asNumber(record.tax_amount, 0),
    line_total: asNumber(record.line_total, 0),
    quantity_returned: asNumber(record.quantity_returned, 0),
    quantity_returnable: asNumber(record.quantity_returnable, 0),
  };
};

const normalizeSaleDetail = (value: unknown): SaleDetail | null => {
  const record = asRecord(value);
  const id = asNumber(record.id, 0);
  if (id <= 0) return null;

  const rawLines = Array.isArray(record.lines) ? record.lines : [];

  return {
    id,
    company_id: asNumber(record.company_id, 0),
    branch_id: asNumber(record.branch_id, 0),
    pos_terminal_id: asNumber(record.pos_terminal_id, 0),
    cash_session_id: asNumber(record.cash_session_id, 0),
    customer_id: asNullableNumber(record.customer_id),
    source_type: asNullableString(record.source_type),
    source_id: asNullableNumber(record.source_id),
    status: asString(record.status, ''),
    subtotal: asNumber(record.subtotal, 0),
    discount_total: asNumber(record.discount_total, 0),
    tax_total: asNumber(record.tax_total, 0),
    grand_total: asNumber(record.grand_total, 0),
    currency_code: asString(record.currency_code, ''),
    exchange_rate: asNumber(record.exchange_rate, 1),
    created_at: asNullableString(record.created_at),
    completed_at: asNullableString(record.completed_at),
    lines: rawLines
      .map((entry) => normalizeSaleDetailLine(entry))
      .filter((entry): entry is SaleDetailLine => Boolean(entry)),
  };
};

const normalizeSaleReturnListItem = (value: unknown): SaleReturnListItem | null => {
  const record = asRecord(value);
  const id = asNumber(record.id, 0);
  if (id <= 0) return null;

  return {
    id,
    company_id: asNumber(record.company_id, 0),
    branch_id: asNumber(record.branch_id, 0),
    sale_id: asNumber(record.sale_id, 0),
    status: asString(record.status, ''),
    document_status: asString(record.document_status, ''),
    operational_status: asString(record.operational_status, ''),
    inventory_status: asString(record.inventory_status, ''),
    refund_status: asString(record.refund_status, ''),
    reason: asNullableString(record.reason),
    total_amount: asNumber(record.total_amount, 0),
    return_date: asString(record.return_date, ''),
    closed_at: asNullableString(record.closed_at),
    sale_status: asNullableString(record.sale_status),
    sale_grand_total: asNumber(record.sale_grand_total, 0),
    lines_count: asNumber(record.lines_count, 0),
    items_qty_total: asNumber(record.items_qty_total, 0),
    to_stock_qty_total: asNumber(record.to_stock_qty_total, 0),
    non_stock_qty_total: asNumber(record.non_stock_qty_total, 0),
  };
};

const normalizeSaleReturnDetailLine = (value: unknown): SaleReturnDetailLine | null => {
  const record = asRecord(value);
  const id = asNumber(record.id, 0);
  if (id <= 0) return null;

  return {
    id,
    sale_return_id: asNumber(record.sale_return_id, 0),
    sale_line_id: asNumber(record.sale_line_id, 0),
    inventory_id: asNumber(record.inventory_id, 0),
    item_sku: asNullableString(record.item_sku),
    item_name: asNullableString(record.item_name),
    quantity_returned: asNumber(record.quantity_returned, 0),
    unit_price: asNumber(record.unit_price, 0),
    discount: asNumber(record.discount, 0),
    tax_rate: asNumber(record.tax_rate, 0),
    tax_amount: asNumber(record.tax_amount, 0),
    line_total: asNumber(record.line_total, 0),
    restock_action: asString(record.restock_action, ''),
    reason: asNullableString(record.reason),
    inventory_apply_status: asString(record.inventory_apply_status, ''),
    ledger_id: asNullableNumber(record.ledger_id),
    inventory_applied_at: asNullableString(record.inventory_applied_at),
  };
};

const normalizeSaleReturnDetail = (value: unknown): SaleReturnDetail | null => {
  const base = normalizeSaleReturnListItem(value);
  if (!base) return null;

  const record = asRecord(value);
  const rawLines = Array.isArray(record.lines) ? record.lines : [];

  return {
    ...base,
    inventory_applied_at: asNullableString(record.inventory_applied_at),
    inventory_error_code: asNullableString(record.inventory_error_code),
    inventory_error_cause: asNullableString(record.inventory_error_cause),
    inventory_idempotency_key: asNullableString(record.inventory_idempotency_key),
    refund_applied_at: asNullableString(record.refund_applied_at),
    refund_error_code: asNullableString(record.refund_error_code),
    refund_error_cause: asNullableString(record.refund_error_cause),
    refund_idempotency_key: asNullableString(record.refund_idempotency_key),
    refund_payment_id: asNullableNumber(record.refund_payment_id),
    closure_blockers: Array.isArray(record.closure_blockers)
      ? record.closure_blockers.filter((entry): entry is string => typeof entry === 'string')
      : [],
    lines: rawLines
      .map((entry) => normalizeSaleReturnDetailLine(entry))
      .filter((entry): entry is SaleReturnDetailLine => Boolean(entry)),
  };
};

const normalizeSaleReturnPageResult = (
  resp?: ApiResponseBase<PageResult<unknown>>,
): PageResult<SaleReturnListItem> => {
  const raw = resp?.data;
  const items = Array.isArray(raw?.items)
    ? raw.items
        .map((entry) => normalizeSaleReturnListItem(entry))
        .filter((entry): entry is SaleReturnListItem => Boolean(entry))
    : [];

  return {
    items,
    total: Number(raw?.total ?? items.length ?? 0),
    page: Number(raw?.page ?? 1),
    page_size: Number(raw?.page_size ?? items.length ?? 0),
  };
};

const normalizeDailyClosurePrintContext = (value: unknown): DailyClosurePrintContext => {
  const record = asRecord(value);
  const businessDate = readBusinessDate(record);
  const rawSessions = Array.isArray(record.sessions) ? record.sessions : [];

  return {
    id: asNumber(record.id, 0),
    business_date: businessDate,
    date: asString(record.date, businessDate),
    company_id: asNumber(record.company_id, 0),
    branch_id: asNumber(record.branch_id, 0),
    cash_initial_base: asNumber(record.cash_initial_base, 0),
    cash_expected_end: asNumber(record.cash_expected_end, 0),
    cash_closing_end: asNumber(record.cash_closing_end, 0),
    cash_end_difference: asNumber(record.cash_end_difference, 0),
    created_by: asNumber(record.created_by, 0),
    created_at: asNullableString(record.created_at),
    notes: asNullableString(record.notes),
    sessions: rawSessions
      .map((entry) => normalizeDailyClosurePrintSessionDetail(entry))
      .filter((entry): entry is DailyClosurePrintSessionDetail => Boolean(entry)),
  };
};

const applyLegacyCompatibleDateFilter = (
  search: URLSearchParams,
  params: Pick<CashSessionFilters, 'business_date' | 'opened_date' | 'closed_date' | 'date'>,
) => {
  const explicitFilter = (
    [
      ['business_date', params.business_date],
      ['opened_date', params.opened_date],
      ['closed_date', params.closed_date],
    ] as Array<[string, string | undefined]>
  ).find((entry) => typeof entry[1] === 'string' && entry[1].trim() !== '');

  if (explicitFilter && explicitFilter[1]) {
    const [field, value] = explicitFilter;
    search.set(field, value);
    return;
  }

  if (params.date) {
    search.set('date', params.date);
  }
};

const applyDailyClosureBusinessDate = (
  search: URLSearchParams,
  params?: Pick<DailyClosureQueryParams, 'business_date' | 'date'>,
) => {
  if (!params) return;
  if (params.business_date) {
    search.set('business_date', params.business_date);
    return;
  }
  if (params.date) {
    search.set('date', params.date);
  }
};

// Construye la query string para endpoints paginados.
const buildPaginationQuery = (params?: PaginationParams): string => {
  if (!params) return '';
  const search = new URLSearchParams();
  if (params.page != null) search.set('page', String(params.page));
  if (params.page_size != null) search.set('page_size', String(params.page_size));
  const q = search.toString();
  return q ? `?${q}` : '';
};

const buildSaleReturnListQuery = (params?: SaleReturnListQuery): string => {
  if (!params) return '';
  const search = new URLSearchParams();
  if (params.page != null) search.set('page', String(params.page));
  if (params.page_size != null) search.set('page_size', String(params.page_size));
  if (params.operational_status) search.set('operational_status', params.operational_status);
  const q = search.toString();
  return q ? `?${q}` : '';
};

const buildCashSessionQuery = (params?: CashSessionFilters): string => {
  if (!params) return '';
  const search = new URLSearchParams();
  if (params.page != null) search.set('page', String(params.page));
  if (params.page_size != null) search.set('page_size', String(params.page_size));
  if (params.status) search.set('status', params.status);
  applyLegacyCompatibleDateFilter(search, params);
  if (params.timezone) search.set('timezone', params.timezone);
  const q = search.toString();
  return q ? `?${q}` : '';
};

const buildDailyClosureQuery = (params?: DailyClosureQueryParams): string => {
  if (!params) return '';
  const search = new URLSearchParams();
  applyDailyClosureBusinessDate(search, params);
  if (params.timezone) search.set('timezone', params.timezone);
  const q = search.toString();
  return q ? `?${q}` : '';
};

// Normaliza la respuesta paginada del backend para evitar nulls/undefined.
const normalizePageResult = <T>(resp?: ApiResponseBase<PageResult<T>>): PageResult<T> => {
  const raw = resp?.data;
  const items = (raw?.items ?? []) as T[];
  return {
    items,
    total: Number(raw?.total ?? items.length ?? 0),
    page: Number(raw?.page ?? 1),
    page_size: Number(raw?.page_size ?? items.length ?? 0),
  };
};

// API RTK Query que orquesta terminales, sesiones, ventas y devoluciones de POS.
export const posApi = createApi({
  reducerPath: 'posApi',
  baseQuery: dynamicBaseQueryWithLogger(BASE_URL),
  tagTypes: ['PosTerminal', 'CashSession', 'Sale', 'SaleReturn'],
  endpoints: (builder) => ({
    // GET /v1/terminals
    listTerminals: builder.query<PageResult<POSTerminal>, PaginationParams>({
      query: (params) => ({
        url: `/v1/terminals${buildPaginationQuery(params)}`,
        method: 'GET',
      }),
      transformResponse: (resp: ApiResponseBase<PageResult<POSTerminal>>) =>
        normalizePageResult(resp),
      transformErrorResponse: handlerErrorResponse('POS.ListTerminals'),
      providesTags: ['PosTerminal'],
    }),
    // GET /v1/cash-sessions
    listCashSessions: builder.query<PageResult<CashSessionRecord>, CashSessionFilters | void>({
      query: (params) => ({
        url: `/v1/cash-sessions${buildCashSessionQuery(params ?? {})}`,
        method: 'GET',
      }),
      transformResponse: (resp: ApiResponseBase<PageResult<unknown>>) =>
        normalizeCashSessionPageResult(resp),
      transformErrorResponse: handlerErrorResponse('POS.ListCashSessions'),
      providesTags: ['CashSession'],
    }),
    // GET /v1/cash-sessions/open
    getOpenCashSession: builder.query<
      CashSessionRecord | null,
      { terminalId: number; timezone?: string }
    >({
      query: ({ terminalId, timezone }) => {
        const params = new URLSearchParams();
        params.set('pos_terminal_id', String(terminalId));
        if (timezone) params.set('timezone', timezone);
        return {
          url: `/v1/cash-sessions/open?${params.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (resp: ApiResponseBase<unknown>) =>
        normalizeCashSessionRecord(resp?.data) ?? null,
      transformErrorResponse: handlerErrorResponse('POS.GetOpenCashSession'),
      providesTags: ['CashSession'],
    }),
    // POST /v1/cash-sessions/open
    openCashSession: builder.mutation<ApiResponseBase<{ id: number }>, OpenCashSessionArgs>({
      query: ({ timezone, ...body }) => {
        const search = new URLSearchParams();
        if (timezone) search.set('timezone', timezone);
        const q = search.toString();
        return {
          url: `/v1/cash-sessions/open${q ? `?${q}` : ''}`,
          method: 'POST',
          body,
        };
      },
      transformErrorResponse: handlerErrorResponse('POS.OpenCashSession'),
      invalidatesTags: ['CashSession'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          notifyAppError(dispatch, err, {
            endpointName: 'POS.OpenCashSession',
            fallbackMessage: 'No se pudo abrir la sesión de caja.',
          });
        }
      },
    }),
    // POST /v1/cash-sessions/close
    closeCashSession: builder.mutation<
      ApiResponseBase<CashSessionCloseSummary>,
      CloseCashSessionInput
    >({
      query: (body) => ({
        url: '/v1/cash-sessions/close',
        method: 'POST',
        body,
      }),
      transformErrorResponse: handlerErrorResponse('POS.CloseCashSession'),
      invalidatesTags: ['CashSession'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          notifyAppError(dispatch, err, {
            endpointName: 'POS.CloseCashSession',
            fallbackMessage: 'No se pudo cerrar la sesión de caja.',
          });
        }
      },
    }),
    // POST /v1/cash-sessions/close/preview
    previewCloseCashSession: builder.mutation<
      CashSessionCloseSummary | null,
      CloseCashSessionPreviewInput
    >({
      query: (body) => ({
        url: '/v1/cash-sessions/close/preview',
        method: 'POST',
        body,
      }),
      transformResponse: (resp: ApiResponseBase<CashSessionCloseSummary>) => resp?.data ?? null,
      transformErrorResponse: handlerErrorResponse('POS.PreviewCloseCashSession'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          notifyAppError(dispatch, err, {
            endpointName: 'POS.PreviewCloseCashSession',
            fallbackMessage: 'No se pudo previsualizar el cierre de caja.',
          });
        }
      },
    }),
    // POST /v1/sales/commit
    createSale: builder.mutation<SaleCommitResult, { sale: SaleInput; idempotencyKey?: string }>({
      query: ({ sale, idempotencyKey }) => {
        const headerKey = (idempotencyKey || generateIdempotencyKey('pos-sale')).slice(0, 100);
        return {
          url: '/v1/sales/commit',
          method: 'POST',
          body: sale,
          headers: {
            'X-Idempotency-Key': headerKey,
          },
        };
      },
      transformResponse: (resp: ApiResponseBase<SaleCommitResult>) => resp.data,
      transformErrorResponse: handlerErrorResponse('POS.CreateSale'),
      invalidatesTags: ['Sale'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          notifyAppError(dispatch, err, {
            endpointName: 'POS.CreateSale',
            fallbackMessage: 'No se pudo registrar la venta.',
          });
        }
      },
    }),
    // GET /v1/sales/{id}
    getSale: builder.query<SaleDetail | null, { id: number }>({
      query: ({ id }) => ({
        url: `/v1/sales/${id}`,
        method: 'GET',
      }),
      transformResponse: (resp: ApiResponseBase<unknown>) => normalizeSaleDetail(resp?.data),
      transformErrorResponse: handlerErrorResponse('POS.GetSale'),
      providesTags: ['Sale'],
    }),
    // GET /v1/sale-returns
    listSaleReturns: builder.query<PageResult<SaleReturnListItem>, SaleReturnListQuery | void>({
      query: (params) => ({
        url: `/v1/sale-returns${buildSaleReturnListQuery(params ?? undefined)}`,
        method: 'GET',
      }),
      transformResponse: (resp: ApiResponseBase<PageResult<unknown>>) =>
        normalizeSaleReturnPageResult(resp),
      transformErrorResponse: handlerErrorResponse('POS.ListSaleReturns'),
      providesTags: ['SaleReturn'],
    }),
    // GET /v1/sale-returns/{id}
    getSaleReturn: builder.query<SaleReturnDetail | null, { id: number }>({
      query: ({ id }) => ({
        url: `/v1/sale-returns/${id}`,
        method: 'GET',
      }),
      transformResponse: (resp: ApiResponseBase<unknown>) => normalizeSaleReturnDetail(resp?.data),
      transformErrorResponse: handlerErrorResponse('POS.GetSaleReturn'),
      providesTags: ['SaleReturn'],
    }),
    // POST /v1/sale-returns
    createSaleReturn: builder.mutation<{ id: number }, SaleReturnInput>({
      query: (body) => ({
        url: '/v1/sale-returns',
        method: 'POST',
        body,
      }),
      transformResponse: (resp: ApiResponseBase<{ id?: number }>) => ({
        id: asNumber(resp?.data?.id, 0),
      }),
      transformErrorResponse: handlerErrorResponse('POS.CreateSaleReturn'),
      invalidatesTags: ['SaleReturn'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          notifyAppError(dispatch, err, {
            endpointName: 'POS.CreateSaleReturn',
            fallbackMessage: 'No se pudo registrar la devolución.',
          });
        }
      },
    }),
    // POST /v1/sale-returns/{id}/inventory-returns
    createSaleReturnInventoryReturn: builder.mutation<
      SaleReturnInventoryReturnResponse,
      { id: number; idempotencyKey?: string }
    >({
      query: ({ id, idempotencyKey }) => ({
        url: `/v1/sale-returns/${id}/inventory-returns`,
        method: 'POST',
        headers: {
          'X-Idempotency-Key': (
            idempotencyKey || generateIdempotencyKey(`sale-return-stock-${id}`)
          ).slice(0, 100),
        },
      }),
      transformResponse: (resp: ApiResponseBase<SaleReturnInventoryReturnResponse>) =>
        resp?.data ?? {
          idempotency_key: '',
          status: '',
          sale_return_id: 0,
          applied_lines: 0,
          inventory_status: '',
        },
      transformErrorResponse: handlerErrorResponse('POS.CreateSaleReturnInventoryReturn'),
      invalidatesTags: ['SaleReturn'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          notifyAppError(dispatch, err, {
            endpointName: 'POS.CreateSaleReturnInventoryReturn',
            fallbackMessage: 'No se pudo registrar el retorno físico a inventario.',
          });
        }
      },
    }),
    // POST /v1/sale-returns/{id}/refunds
    createSaleReturnRefund: builder.mutation<
      SaleReturnRefundResponse,
      { id: number; body: CreateSaleReturnRefundInput; idempotencyKey?: string }
    >({
      query: ({ id, body, idempotencyKey }) => ({
        url: `/v1/sale-returns/${id}/refunds`,
        method: 'POST',
        body,
        headers: {
          'X-Idempotency-Key': (
            idempotencyKey || generateIdempotencyKey(`sale-return-refund-${id}`)
          ).slice(0, 100),
        },
      }),
      transformResponse: (resp: ApiResponseBase<SaleReturnRefundResponse>) =>
        resp?.data ?? {
          idempotency_key: '',
          status: '',
          sale_return_id: 0,
          refunded_amount: 0,
          refund_status: '',
        },
      transformErrorResponse: handlerErrorResponse('POS.CreateSaleReturnRefund'),
      invalidatesTags: ['SaleReturn'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          notifyAppError(dispatch, err, {
            endpointName: 'POS.CreateSaleReturnRefund',
            fallbackMessage: 'No se pudo registrar el reembolso de la devolución.',
          });
        }
      },
    }),
    // POST /v1/cash-daily-closures
    createDailyClosure: builder.mutation<
      DailyClosureResult,
      DailyClosureQueryParams & {
        notes?: string | null;
        idempotencyKey?: string;
      }
    >({
      query: ({ business_date, date, notes, timezone, idempotencyKey }) => {
        const search = new URLSearchParams();
        if (timezone) search.set('timezone', timezone);
        const q = search.toString();
        const body: { business_date?: string; date?: string; notes?: string | null } = { notes };
        if (business_date) {
          body.business_date = business_date;
        } else if (date) {
          body.date = date;
        }
        return {
          url: `/v1/cash-daily-closures${q ? `?${q}` : ''}`,
          method: 'POST',
          body,
          headers: {
            'X-Idempotency-Key': (
              idempotencyKey || generateIdempotencyKey('pos-daily-close')
            ).slice(0, 128),
          },
        };
      },
      transformResponse: (resp: ApiResponseBase<unknown>) =>
        normalizeDailyClosureResult(resp?.data),
      transformErrorResponse: handlerErrorResponse('POS.CreateDailyClosure'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          notifyAppError(dispatch, err, {
            endpointName: 'POS.CreateDailyClosure',
            fallbackMessage: 'No se pudo generar el corte diario.',
          });
        }
      },
    }),
    // GET /v1/cash-daily-closures/preview
    getDailyClosurePreview: builder.query<
      DailyClosurePreviewResult,
      DailyClosureQueryParams | void
    >({
      query: (params) => {
        return {
          url: `/v1/cash-daily-closures/preview${buildDailyClosureQuery(params ?? {})}`,
          method: 'GET',
        };
      },
      transformResponse: (resp: ApiResponseBase<unknown>) =>
        normalizeDailyClosurePreviewResult(resp?.data),
      transformErrorResponse: handlerErrorResponse('POS.GetDailyClosurePreview'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          notifyAppError(dispatch, err, {
            endpointName: 'POS.GetDailyClosurePreview',
            fallbackMessage: 'No se pudo obtener la previsualización del corte diario.',
          });
        }
      },
    }),
    // GET /v1/cash-daily-closures
    getDailyClosureExists: builder.query<
      DailyClosureExistsResult,
      DailyClosureQueryParams | void
    >({
      query: (params) => {
        return {
          url: `/v1/cash-daily-closures${buildDailyClosureQuery(params ?? {})}`,
          method: 'GET',
        };
      },
      transformResponse: (resp: ApiResponseBase<unknown>) =>
        normalizeDailyClosureExistsResult(resp?.data),
      transformErrorResponse: handlerErrorResponse('POS.GetDailyClosureExists'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          notifyAppError(dispatch, err, {
            endpointName: 'POS.GetDailyClosureExists',
            fallbackMessage: 'No se pudo verificar el corte diario.',
          });
        }
      },
    }),
    // GET /v1/cash-daily-closures/{id}/print-context
    getDailyClosurePrintContext: builder.query<DailyClosurePrintContext, { id: number }>({
      query: ({ id }) => ({
        url: `/v1/cash-daily-closures/${id}/print-context`,
        method: 'GET',
      }),
      transformResponse: (resp: ApiResponseBase<unknown>) =>
        normalizeDailyClosurePrintContext(resp?.data),
      transformErrorResponse: handlerErrorResponse('POS.GetDailyClosurePrintContext'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          notifyAppError(dispatch, err, {
            endpointName: 'POS.GetDailyClosurePrintContext',
            fallbackMessage: 'No se pudo obtener el contexto de impresión del corte diario.',
          });
        }
      },
    }),
    // GET /v1/sales/by-sessions
    getSalesBySessions: builder.query<SalesBySessionsResult, SalesBySessionsArgs>({
      query: ({ cash_session_ids, status }) => ({
        url: '/v1/sales/by-sessions',
        method: 'GET',
        params: {
          cash_session_ids: Array.isArray(cash_session_ids)
            ? cash_session_ids.join(',')
            : cash_session_ids,
          status,
        },
      }),
      transformResponse: (resp: ApiResponseBase<SalesBySessionsResult>) =>
        resp?.data ?? { sale_ids: [], total: 0 },
      transformErrorResponse: handlerErrorResponse('POS.SalesBySessions'),
      providesTags: ['Sale'],
    }),
    // POST /v1/sales/summary/batch
    postSalesSummaryBatch: builder.mutation<SaleSummaryBatchResult, SaleSummaryBatchRequest>({
      query: (body) => ({
        url: '/v1/sales/summary/batch',
        method: 'POST',
        body,
      }),
      transformResponse: (resp: ApiResponseBase<SaleSummaryBatchResult>) =>
        resp?.data ?? { items: [], missing_ids: [] },
      transformErrorResponse: handlerErrorResponse('POS.SalesSummaryBatch'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          notifyAppError(dispatch, err, {
            endpointName: 'POS.SalesSummaryBatch',
            fallbackMessage: 'No se pudo obtener el resumen comercial de ventas.',
          });
        }
      },
    }),
  }),
});

export const {
  useListTerminalsQuery,
  useListCashSessionsQuery,
  useGetOpenCashSessionQuery,
  useOpenCashSessionMutation,
  useCloseCashSessionMutation,
  usePreviewCloseCashSessionMutation,
  useCreateDailyClosureMutation,
  useGetDailyClosurePreviewQuery,
  useGetDailyClosureExistsQuery,
  useLazyGetDailyClosureExistsQuery,
  useLazyGetDailyClosurePrintContextQuery,
  useCreateSaleMutation,
  useGetSaleQuery,
  useLazyGetSaleQuery,
  useListSaleReturnsQuery,
  useGetSaleReturnQuery,
  useCreateSaleReturnMutation,
  useCreateSaleReturnInventoryReturnMutation,
  useCreateSaleReturnRefundMutation,
  useGetSalesBySessionsQuery,
  usePostSalesSummaryBatchMutation,
} = posApi;
